import {
    ChangeDetectionStrategy,
    Component,
    inject,
    Input,
    ViewEncapsulation,
    ElementRef,
    effect,
    input,
    DestroyRef,
} from '@angular/core';
import { AbstractControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors } from '@angular/forms';
import { ControlValueAccessorValidatorBase } from '@fi-app/shared/control-value-accessor-validator-base';
import { isDefined } from '@fi-app/shared/utils/is-defined';
import { BehaviorSubject } from 'rxjs';
import { FiOnboardingBuilderStoreService } from '@fi-app/fi-onboarding/fi-onboarding-builder/fi-onboarding-builder-store.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'fi-onboarding-sharing-radio-group',
    templateUrl: './fi-onboarding-sharing-radio-group.component.html',
    styleUrls: ['./fi-onboarding-sharing-radio-group.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-onboarding-sharing-radio-group' },
    standalone: false,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: FiOnboardingSharingRadioGroupComponent,
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: FiOnboardingSharingRadioGroupComponent,
            multi: true,
        },
    ],
})
export class FiOnboardingSharingRadioGroupComponent extends ControlValueAccessorValidatorBase<boolean> {
    @Input() scrollableElementRefs: ElementRef<HTMLElement>[] = [];

    public hidden = input<boolean>(false);
    private _hiddenChangeEffect = effect(() => {
        if (this.hidden()) {
            this.toggleTooltipVisibility$.next(false);
        } else {
            this.setShowErrorTooltip();
        }
    });

    private readonly fiOnboardingBuilderStoreService = inject(FiOnboardingBuilderStoreService);
    private readonly destroyRef = inject(DestroyRef);

    protected toggleTooltipVisibility$ = new BehaviorSubject<boolean>(false);

    private _isFormSubmitted = false;
    private _isControlStatusSubscribed = false;
    private _formSubmitEffect = effect(() => {
        const formSubmittedCount = this.fiOnboardingBuilderStoreService.formSubmitted();
        if (!formSubmittedCount || this._isFormSubmitted) {
            return;
        }
        this._isFormSubmitted = true;
        this.trySubscribeOnControlStatusChange();
        this.onInvalidStatusUpdate();
    });

    private trySubscribeOnControlStatusChange(): void {
        if (!this._isFormSubmitted || !this.controlComponent || this._isControlStatusSubscribed) {
            return;
        }
        this._isControlStatusSubscribed = true;
        this.controlComponent.statusChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.onInvalidStatusUpdate();
        });
    }

    public doValidate(c: AbstractControl): ValidationErrors {
        this.trySubscribeOnControlStatusChange();
        return {
            'no-sharing-selected': !isDefined(this.ngModel),
        };
    }

    private onInvalidStatusUpdate(): void {
        if (this.hidden()) {
            this.toggleTooltipVisibility$.next(false);
            return;
        }
        this.setShowErrorTooltip();
    }

    private setShowErrorTooltip(): void {
        if (!this._isFormSubmitted) {
            return;
        }
        const errors = this.controlComponent?.errors ?? {};
        const hasErrors = Object.keys(errors).length > 0;
        this.toggleTooltipVisibility$.next(hasErrors);
    }
}
